<template>
  <div class="filters">
    <div class="filters__groups">
      <div
        v-for="filter in filtersTemplate"
        :key="filter.field"
        class="filter-group"
        :class="{ 'filter-group--full': filter.field === 'status' }"
      >
        <span class="filter-title">{{ filter.title }}</span>
        <div class="filter-group__chips">
          <button
            v-for="searchTerm in filter.searchTerms"
            :key="searchTerm.value"
            type="button"
            class="chip-button"
            :aria-pressed="isActiveSearchTerm(filter.field, searchTerm.value) ? 'true' : 'false'"
            @click="handleFilterClick(filter.field, searchTerm.value)"
          >
            <AppChip :color="isActiveSearchTerm(filter.field, searchTerm.value) ? 'brand' : 'muted'">
              {{ searchTerm.text }}
            </AppChip>
          </button>
        </div>
      </div>

      <div class="filter-group">
        <!-- An AppSelect without a label points aria-labelledby at its own id, so the title shares that id. -->
        <span id="handler-filter" class="filter-title">Handläggare</span>
        <AppSelect
          id="handler-filter"
          :model-value="filters.brokerPersonnelUserId ?? ''"
          :options="personnelOptions"
          :disabled="isLoadingPersonnel"
          placeholder="Alla"
          slim
          @update:model-value="handleHandlerChange"
        />
      </div>
    </div>

    <button type="button" class="clear-filters" @click="clearFilters">
      <SvgIcon :path="mdiClose" size="16" />
      <span>Rensa filter</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { mdiClose } from '@mdi/js'
import { AppChip, AppSelect, SvgIcon } from '@staff/ui'

import type { DistributorApplicationListFilters } from '@/composables/distributorApplicationSearch'
import { usePersonnelDirectory } from '@/composables/usePersonnelDirectory'
import { DistributorApplicationLifecycleStatus, DistributorApplicationType } from '@/services/application'
import { translateApplicationEnum } from '@/utils/applicationDisplay'
import { WORK_RESPONSIBILITY_PARTY_LABELS } from '@staff/shared'

type FilterField = keyof DistributorApplicationListFilters

const props = defineProps<{
  filters: DistributorApplicationListFilters
}>()

const emit = defineEmits<{
  (e: 'filters', value: DistributorApplicationListFilters): void
}>()

const { t } = useI18n()
const { personnelOptions, isLoadingPersonnel } = usePersonnelDirectory()

// TODO: Use DAPI metadata for these options once applicationStatuses/applicationTypes reflect the distributor list contract.
const statusSearchTerms = computed(() =>
  Object.values(DistributorApplicationLifecycleStatus)
    .filter((value) => value !== 'draft')
    .map((value) => ({
      text: translateApplicationEnum(t, 'APPLICATION_STATUS', value),
      value,
    })),
)

const typeSearchTerms = computed(() =>
  Object.values(DistributorApplicationType).map((value) => ({
    text: translateApplicationEnum(t, 'APPLICATION_TYPE', value),
    value,
  })),
)

const workResponsibilityPartySearchTerms = Object.entries(WORK_RESPONSIBILITY_PARTY_LABELS).map(([key, label]) => ({
  text: label,
  value: key,
}))

const filtersTemplate = computed(() => [
  {
    field: 'status' as const,
    title: 'Status',
    searchTerms: statusSearchTerms.value,
  },
  {
    field: 'type' as const,
    title: 'Typ av ansökan',
    searchTerms: typeSearchTerms.value,
  },
  {
    field: 'workResponsibilityParty' as const,
    title: 'Vem har bollen',
    searchTerms: workResponsibilityPartySearchTerms,
  },
  {
    field: 'hasUnreadMessages' as const,
    title: 'Meddelanden',
    searchTerms: [{ text: 'Olästa meddelanden', value: 'true' }],
  },
  {
    field: 'hasOpenConversations' as const,
    title: 'Konversationer',
    searchTerms: [{ text: 'Öppna konversationer', value: 'true' }],
  },
  {
    field: 'hasOpenRequiredAdditions' as const,
    title: 'Kompletteringar',
    searchTerms: [{ text: 'Öppna obligatoriska', value: 'true' }],
  },
  {
    field: 'isHighPrio' as const,
    title: 'Prioritet',
    searchTerms: [{ text: 'Hög prio', value: 'true' }],
  },
])

const getActiveSearchTerm = (field: FilterField) => {
  const value = props.filters[field]
  if (typeof value === 'boolean') return value ? 'true' : undefined
  return value
}

const isActiveSearchTerm = (field: FilterField, value: string) => getActiveSearchTerm(field) === value

const handleFilterClick = (field: FilterField, value: string) => {
  const currentValue = getActiveSearchTerm(field)
  const nextFilters = { ...props.filters }

  if (currentValue === value) {
    delete nextFilters[field]
  } else if (value === 'true') {
    nextFilters[field] = true as never
  } else {
    nextFilters[field] = value as never
  }

  emit('filters', nextFilters)
}

const handleHandlerChange = (userId: string) => {
  const nextFilters = { ...props.filters }

  if (userId) {
    nextFilters.brokerPersonnelUserId = userId
  } else {
    delete nextFilters.brokerPersonnelUserId
  }

  emit('filters', nextFilters)
}

const clearFilters = () => {
  emit('filters', {})
}
</script>

<style scoped lang="scss">
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.filters__groups {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: 16px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group--full {
  width: 100%;
}

.filter-title {
  color: var(--color-fg-default);
  font-size: 0.875rem;
  font-weight: 500;
}

.filter-group__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip-button {
  display: inline-flex;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}

.chip-button:focus-visible,
.clear-filters:focus-visible {
  outline: 2px solid var(--color-brand-default);
  outline-offset: 2px;
}

.clear-filters {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-fg-default);
  cursor: pointer;
  font: inherit;
}
</style>
